import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { IReduxState } from 'src/store';
import { Notification, isChecklistComplete } from 'src/utils';
import { ITask, TaskMaintenanceType, TaskStage } from 'src/interfaces/task';
import TasksService from 'src/services/tasks.service';

import { IProps } from './board.type';
import { ICard } from 'src/interfaces/board';

const useBoard = (props: IProps) => {
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(false);
  const [spinning, setSpinning] = useState(false);

  const [tasks, setTasks] = useState<ITask[] | undefined>(undefined);
  const dispatch = useDispatch();
  const listsById = useSelector((state: IReduxState) => state.board.listsById);
  const cardsById = useSelector((state: IReduxState) => state.board.cardsById);
  const filter = useSelector((state: IReduxState) => state.tasks.filter);

  const handleTasks = async () => {
    setLoading(true);

    dispatch({
      type: 'CLEAR_BOARD',
    });

    try {
      setLoading(true);

      const response = await props.fetchTasks(filter);

      if (response) {
        setTasks(response);
        boardSeeder(response);
      }
    } catch (err: any) {
      Notification.error({
        message:
          'Ocorreu um erro ao carregar as tarefas, tente novamente mais tarde.',
      });

      return false;
    } finally {
      setLoading(false);
    }
  };

  const boardSeeder = async (data: ITask[]) => {
    for await (const item of data) {
      let card = {
        listId: item.stage,
        cardId: `${item.id}`,
        cardText: `#${item.id}`,
        ...item,
      };

      dispatch({
        type: 'ADD_LIST_CARD',
        payload: card,
      });

      dispatch({
        type: 'ADD_CARD',
        payload: card,
      });
    }
  };

  const updateStageTask = async (taskId: string, stage: TaskStage) => {
    try {
      const response = await props.updateStageTask(taskId, stage);
      return !!response;
    } catch (err: any) {
      Notification.error({
        message:
          'Ocorreu um erro ao carregar as tarefas, tente novamente mais tarde.',
      });

      return false;
    }
  };

  const handleDragEnd = async ({ source, destination }: any) => {
    if (!destination) return;

    if (
      source.index !== destination.index ||
      source.droppableId !== destination.droppableId
    ) {
      let taskId: any = Array.from(listsById[source.droppableId].cards).splice(
        source.index,
        1
      )[0];

      let card: ICard = cardsById[taskId];
      const isPreventive = card.maintenanceType === TaskMaintenanceType.PREVENTIVE;

      if (destination.droppableId === '5' && !isPreventive) {
        return Notification.error({
          message: 'Apenas OS preventivas podem ser movidas para PREVENTIVA AGENDADA.',
        });
      }

      if (destination.droppableId === '0' && isPreventive) {
        return Notification.error({
          message: 'OS preventivas não podem ser movidas para PENDENTE.',
        });
      }

      switch (destination.droppableId) {
        case '1':
          if (!card.technicianTasks.length) {
            return Notification.error({
              message:
                'Um técnico ou mais precisa estar vinculado a ordem de serviço para ser iniciada.',
            });
          }
          break;
        case '3':
          if (isPreventive) {
            try {
              const checklistItems = await TasksService.listChecklistItems(
                String(taskId)
              );
              if (
                !isChecklistComplete({
                  maintenanceType: card.maintenanceType,
                  checklistItems,
                })
              ) {
                return Notification.error({
                  message:
                    'Conclua todas as opções do checklist preventivo antes de finalizar a OS.',
                });
              }
            } catch (err) {
              return Notification.error({
                message:
                  'Não foi possível validar o checklist preventivo. Tente novamente.',
              });
            }
          }
          break;
      }

      const updated = await updateStageTask(taskId, destination.droppableId);

      if (!updated) {
        return;
      }

      dispatch({
        type: 'MOVE_LIST_CARD',
        payload: {
          sourceListId: source.droppableId,
          destListId: destination.droppableId,
          oldCardIndex: source.index,
          newCardIndex: destination.index,
        },
      });
    }
  };

  const handleReload = () => {
    setReload(!reload);
  };

  return {
    handleTasks,
    loading,
    tasks,
    handleDragEnd,
    reload,
    handleReload,
    spinning,
    setSpinning,
    filter,
  };
};

export default useBoard;
